import React, { useRef, useState, useEffect } from 'react'
import { Box, Flex, Group, Button, Select, Textarea, ActionIcon, Paper, Switch } from '@mantine/core'
import { DateInput, TimeInput } from '@mantine/dates'
import { IconClock } from '@tabler/icons-react'
import { getAllStatusesList } from '../../../../../services/status'
import { addPackageShipmentStatus, updatePackageShipmentStatus } from '../../../../../services/package'
import { validation } from '../../../../../plugins/validation'
import LoadingData from '../../../../ui/LoadingData'
import dayjs from 'dayjs'
import { useParams } from 'react-router-dom'
import { notificationSuccess, notificationError } from '../../../../ui/Notifications'

const defaultVal = {
  status_id: null,
  date: new Date(),
  time: '00:00',
  comment: '',
  notify: false
}

const formValidation = {
  status_id: {
    isError: false,
    message: ''
  },
  date: {
    isError: false,
    message: ''
  },
  time: {
    isError: false,
    message: ''
  },
}

const FormAdjustTracking = ({ dataTracking, onCloseForm, reloadList }) => {
  const params = useParams()
  const packageId = params.package_id
  const timeRef = useRef(null)
  const [loadingStatus, setLoadingStatus] = useState(true)
  const [loadingForm, setLoadingForm] = useState(false)
  const [statusList, setStatusList] = useState([])
  const [formData, setFormData] = useState(defaultVal)
  const [validationForm, setValidationForm] = useState(formValidation)

  const submitTrackingStatus = async (form) => {
    setLoadingForm(true)
    setValidationForm(formValidation)
    let methodFunction = null
    let titleMessageSuccess = ''
    let captionMessageSuccess = ''
    let titleMessageError = ''
    let captionMessageError = ''
    let payload = {}
    const validateForm = {
      status_id: form.status_id,
      date: form.date,
      time: form.time
    }
    const isError = validation(validateForm, setValidationForm)
    if (isError) {
      setLoadingForm(false)
      return
    }
    if (dataTracking === null) {
      payload = {
        status_id: Number(form.status_id),
        date: `${dayjs(form.date).format('YYYY-MM-DD')} ${form.time + ':00'}`,
        notify: form.notify,
        comment: form.comment
      }
      methodFunction = addPackageShipmentStatus(packageId, payload)
      titleMessageSuccess = 'Add Tracking Status Successfully'
      captionMessageSuccess = 'You have been added new tracking status'
      titleMessageError = 'Add Tracking Status Error'
    } else {
      payload = {
        status_id: Number(form.status_id),
        date: `${dayjs(form.date).format('YYYY-MM-DD')} ${form.time + ':00'}`,
        statusable_id: form.id,
        comment: form.comment
      }
      methodFunction = updatePackageShipmentStatus(packageId, payload)
      titleMessageSuccess = 'Update Tracking Status Successfully'
      captionMessageSuccess = 'You have been updated data tracking status'
      titleMessageError = 'Update Tracking Status Error'
    }
    try {
      const response = await methodFunction
      if (response.success) {
        onCloseForm()
        reloadList()
        notificationSuccess(titleMessageSuccess, captionMessageSuccess)
      }
    } catch (error) {
      console.log(error)
      const errorMessage = error.response.data.message
      captionMessageError = Object.keys(errorMessage) ? errorMessage : 'Please recheck your form'
      notificationError(titleMessageError, captionMessageError)
    } finally {
      setLoadingForm(false)
    }
  }

  const handleGetStatuses = async () => {
    setLoadingStatus(true)
    const params = {
      'filter[status_type][is]': 3
    }
    try {
      const response = await getAllStatusesList(params)
      if (response.success) {
        const dataStatus = response.data

        const remapData = dataStatus.map((val) => {
          return {
            value: `${val.attributes.id}`,
            label: val.attributes.name.charAt(0).toUpperCase() + val.attributes.name.slice(1)
          }
        })

        setStatusList(remapData)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoadingStatus(false)
    }
  }

  useEffect(() => {
    handleGetStatuses()
  }, [])

  useEffect(() => {
    if (dataTracking !== null) {
      handleSetForm(dataTracking)
    }
  }, [dataTracking])

  const handleSetForm = (data) => {
    const detail = {
      ...data,
      status_id: `${data.status_id}`,
      date: new Date(data.date.split(' ')[0]),
      time: data.date.split(' ')[1].slice(0, 5)
    }
    setFormData(detail)
  }

  const handleChangeForm = (name, val) => {
    setFormData((oldVal) => ({ ...oldVal, [name]: val}))
  }

  const pickerControl = () => {
    return (
      <ActionIcon variant="subtle" color="gray" onClick={() => timeRef.current?.showPicker()}>
        <IconClock size={16} stroke={1.5} />
      </ActionIcon>
    )
  }

  const mappingFormNotify = (data) => {
    if (data === null) {
      return (
        <Box mb='sm'>
          <Paper shadow="xs" withBorder p="md">
            <Switch
              checked={formData.notify}
              onChange={(val) => handleChangeForm('notify', val.currentTarget.checked)}
              label="Notify Customer (App, Email, WhatsApp)"
              description="If you enabled this, Customer will be receive Notification Message."
              size="xs"
            />
          </Paper>
        </Box>
      )
    }
  }

  const loadFormTracking = () => {
    return (
      <Box>
        <Box mb='sm'>
          <Select
            name='status_id'
            value={formData.status_id}
            label='Tracking Status'
            placeholder='Select Status'
            data={statusList}
            onChange={(val) => handleChangeForm('status_id', val)}
            error={validationForm.status_id.isError ? `${validationForm.status_id.message}` : ''}
            size='xs'
            allowDeselect={false}
            withAsterisk
            clearable
          />
        </Box>
        <Box mb='sm'>
          <DateInput
            name='date'
            value={formData.date}
            label='Date'
            placeholder='DD-MM-YYYY'
            valueFormat="DD-MM-YYYY"
            onChange={(val) => handleChangeForm('date', val)}
            error={validationForm.date.isError ? `${validationForm.date.message}` : ''}
            size='xs'
            clearable
            withAsterisk
          />
        </Box>
        <Box mb='sm'>
          <TimeInput
            ref={timeRef}
            name='time'
            value={formData.time}
            label='Time'
            onChange={(val) => handleChangeForm('time', val.currentTarget.value)}
            rightSection={pickerControl()}
            error={validationForm.time.isError ? `${validationForm.time.message}` : ''}
            size='xs'
            clearable
            withAsterisk
          />
        </Box>
        <Box mb='sm'>
          <Textarea
            name='comment'
            label="Comment"
            placeholder="Input comment"
            value={formData.comment}
            onChange={(val) => handleChangeForm('comment', val.target.value)}
            size='xs'
            autosize
            minRows={4}
            maxRows={4}
          />
        </Box>
        {mappingFormNotify(dataTracking)}
        <Box mt={20}>
          <Flex justify='flex-end'>
            <Group>
              <Button size='xs' variant="outline" onClick={onCloseForm}>Close</Button>
              <Button size='xs' loading={loadingForm} variant="filled" onClick={() => submitTrackingStatus(formData)}>{dataTracking ? 'Update' : 'Create'}</Button>
            </Group>
          </Flex>
        </Box>
      </Box>
    )
  }

  return (
    <Box>
      {loadingStatus ? <LoadingData /> : loadFormTracking()}
    </Box>
  )
}

export default FormAdjustTracking